// src/store/useTeamStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Team, TeamSlot, StatBlock } from '../core/types';
import { EV_MAX, EV_TOTAL_MAX, makeDefaultEvs, makeDefaultIvs } from '../core/stats';
import { SPIRIT_MAP } from '../data/mock-spirits';
export const TEAM_SIZE = 6;
export const MAX_SKILLS_PER_SLOT = 4;

function createEmptySlot(): TeamSlot {
  return {
    spiritId: null,
    nature: 'hardy',
    level: 60,
    ivs: makeDefaultIvs(),
    evs: makeDefaultEvs(),
    skills: [],
    boosts: { atk: 0, spa: 0, def: 0, spd: 0 }
  };
}

function createEmptyTeam(): Team {
  return {
    id:
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : String(Date.now()),
    name: '未命名队伍',
    slots: Array.from({ length: TEAM_SIZE }, createEmptySlot),
  };
}

interface TeamState {
  team: Team;
  setTeamName: (name: string) => void;
  setSlotSpirit: (index: number, spiritId: string | null) => void;
  setSlotNature: (index: number, nature: string) => void;
  setSlotLevel: (index: number, level: number) => void;
  setSlotIv: (index: number, key: keyof StatBlock, value: number) => void;
  setSlotEv: (index: number, key: keyof StatBlock, value: number) => void;
  setSlotBaseStat: (index: number, key: keyof StatBlock, value: number) => void;
  resetSlotBaseStats: (index: number) => void;
  toggleSlotSkill: (index: number, skillId: string) => void;
  clearSlot: (index: number) => void;
  resetTeam: () => void;
  setSlotBoost: (index: number, group: 'attack' | 'defense', level: number) => void;
}

export const useTeamStore = create<TeamState>()(
  persist(
    (set) => ({
      team: createEmptyTeam(),
      setTeamName: (name) =>
        set((s) => ({ team: { ...s.team, name } })),

      setSlotSpirit: (index, spiritId) =>
        set((s) => {
          const slots = [...s.team.slots];
          const slot = { ...slots[index], spiritId };
          slot.skills = [];
          slots[index] = slot;
          return { team: { ...s.team, slots } };
        }),

      setSlotNature: (index, nature) =>
        set((s) => {
          const slots = [...s.team.slots];
          slots[index] = { ...slots[index], nature };
          return { team: { ...s.team, slots } };
        }),

      setSlotLevel: (index, level) =>
        set((s) => {
          const safe = Math.max(1, Math.min(100, level));
          const slots = [...s.team.slots];
          slots[index] = { ...slots[index], level: safe };
          return { team: { ...s.team, slots } };
        }),

      setSlotBaseStat: (index, key, value) =>
        set((s) => {
          const slots = [...s.team.slots];
          const slot = { ...slots[index] };
          const spirit = slot.spiritId ? SPIRIT_MAP[slot.spiritId] : null;
          const current =
            slot.customBaseStats ?? spirit?.baseStats ?? {
              hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0,
            };
          const safe = Math.max(0, Math.min(200, value));
          slot.customBaseStats = { ...current, [key]: safe };
          slots[index] = slot;
          return { team: { ...s.team, slots } };
        }),

      resetSlotBaseStats: (index) =>
        set((s) => {
          const slots = [...s.team.slots];
          const slot = { ...slots[index] };
          delete slot.customBaseStats;
          slots[index] = slot;
          return { team: { ...s.team, slots } };
        }),

      setSlotIv: (index, key, value) =>
        set((s) => {
          const safe = Math.max(0, Math.min(EV_MAX, value));
          const slots = [...s.team.slots];
          const slot = { ...slots[index], ivs: { ...slots[index].ivs, [key]: safe } };
          slots[index] = slot;
          return { team: { ...s.team, slots } };
        }),

      setSlotEv: (index, key, value) =>
        set((s) => {
          const slots = [...s.team.slots];
          const slot = { ...slots[index] };
          const newValue = Math.max(0, Math.min(EV_MAX, value));
          const othersTotal = (Object.keys(slot.evs) as (keyof StatBlock)[])
            .filter((k) => k !== key)
            .reduce((sum, k) => sum + slot.evs[k], 0);
          const maxForThis = Math.min(EV_MAX, EV_TOTAL_MAX - othersTotal);
          const final = Math.min(newValue, maxForThis);
          slot.evs = { ...slot.evs, [key]: final };
          slots[index] = slot;
          return { team: { ...s.team, slots } };
        }),

      toggleSlotSkill: (index, skillId) =>
        set((s) => {
          const slots = [...s.team.slots];
          const slot = { ...slots[index] };
          const skills = [...slot.skills];
          const pos = skills.indexOf(skillId);
          if (pos >= 0) {
            skills.splice(pos, 1);
          } else if (skills.length < MAX_SKILLS_PER_SLOT) {
            skills.push(skillId);
          } else {
            return s;
          }
          slot.skills = skills;
          slots[index] = slot;
          return { team: { ...s.team, slots } };
        }),

      // ==========新增强化等级Action============
      setSlotBoost: (index, group, rawLevel) => set((state) => {
        const safeLv = Math.max(-20, Math.min(20, rawLevel));
        const slots = [...state.team.slots];
        const slot = { ...slots[index] };
        const oldBoost = slot.boosts ?? { atk: 0, spa: 0, def: 0, spd: 0 };
        if (group === "attack") {
          slot.boosts = { ...oldBoost, atk: safeLv, spa: safeLv };
        } else {
          slot.boosts = { ...oldBoost, def: safeLv, spd: safeLv };
        }
        slots[index] = slot;
        return { team: { ...state.team, slots } };
      }),

      clearSlot: (index) =>
        set((s) => {
          const slots = [...s.team.slots];
          slots[index] = createEmptySlot();
          return { team: { ...s.team, slots } };
        }),

      resetTeam: () => set({ team: createEmptyTeam() }),
    }),
    {
      name: 'rococalc-team',
      version: 1,
    },
  ),
);

/** 便捷选择器：拿到某个槽位的实际精灵对象（可能为 null） */
export function useSlotSpirit(index: number) {
  return useTeamStore((s) => {
    const id = s.team.slots[index]?.spiritId;
    return id ? SPIRIT_MAP[id] ?? null : null;
  });
}
